const post = {
  slug: 'double-entry-ledger-that-never-loses-money',
  number: 6,
  title: 'Designing a Ledger That Never Loses a Cent',
  date: '2026-06-28',
  readTime: '8 min read',
  views: 127,
  category: 'backend',
  tags: ['Backend', 'Fintech', 'PostgreSQL'],
  excerpt:
    'When you move money that belongs to other people, "mostly correct" does not exist. This is how I built and reasoned about a ledger that balances every time, drawn from years on a payments platform.',
  cover: null,
  content: `
The scariest ticket I ever picked up said "user balance is off by 1,200 naira." Not a crash, not an outage, just a number that was quietly wrong. On a payments platform that is the worst kind of bug, because nothing is on fire and yet someone's money is missing, and you cannot ship your way out of it with a hotfix and an apology. You have to be able to prove where every cent went.

That ticket, and a few like it, is why I think about ledgers the way I do. When you move money that belongs to other people, "mostly correct" is not a category that exists. A reporting dashboard can be a little off and nobody dies. A ledger that is a little off is a ledger that is wrong, and the difference between a good payments system and a frightening one is whether you can always answer the question "is this correct, and how do you know."

This is the set of principles I keep coming back to, drawn from years building this at Stax. It is not a framework you install. It is a way of thinking that makes correctness checkable instead of hoped for.

## Double entry is not optional

The oldest idea in accounting is also the best idea in financial software. Every transaction is recorded as at least two entries that sum to zero. Money never appears or vanishes, it only moves from one account to another. A transfer is a debit on one account and a matching credit on another, and the two always balance.

\`\`\`text
Transfer of 5,000 from wallet A to wallet B

  account     debit     credit
  --------------------------------
  wallet_A     5,000
  wallet_B               5,000
  --------------------------------
  total        5,000     5,000   -> balances to zero
\`\`\`

The reason this matters is not tradition, it is that correctness becomes provable. At any moment you can sum every entry in the entire system, and if the total is not zero, something is broken and you know it immediately, without a customer having to tell you. You are not trusting that your code is right. You are continuously proving that it is, and that property is worth more than any amount of careful coding.

## A balance is derived, never stored

The tempting shortcut, the one I have watched cause more incidents than any other, is to keep a \`balance\` column on the account and update it on every transaction. Do not do this.

The instant two transactions touch the same account at once, or one update succeeds while another fails halfway, your stored balance and your actual entries disagree. Now you have a number that lies, and worse, it lies quietly. The entries are correct, the balance column is wrong, and nothing tells you until someone notices money is off.

A balance is the sum of an account's entries. That is its definition, so let the database compute it from the entries, which are the source of truth.

\`\`\`sql
SELECT COALESCE(SUM(credit) - SUM(debit), 0) AS balance
FROM ledger_entries
WHERE account_id = $1;
\`\`\`

If summing on every read becomes too slow at scale, you cache the result deliberately, with a clear way to recompute it from the entries at any time. The cache is an optimization you can always rebuild. The entries are the truth you never overwrite. Keep that boundary sharp and a whole class of "the balance is wrong" bugs simply cannot happen.

## Let the database do the hard part

Relational databases were built for exactly this work, and people reach past their best features constantly. Two of them carried most of the weight for me.

**Transactions.** Writing both sides of a transfer inside a single database transaction means it either fully happens or fully does not. There is no universe where the debit landed and the credit did not. This is the entire reason double entry is safe in practice, and it is one line of intent.

\`\`\`sql
BEGIN;
  INSERT INTO ledger_entries (account_id, debit)  VALUES ($1, $3);
  INSERT INTO ledger_entries (account_id, credit) VALUES ($2, $3);
COMMIT;   -- both, or neither
\`\`\`

**Constraints.** Make the database itself refuse to store something invalid. A debit and a credit that are both positive. An amount that should never be negative. The database is your last line of defence, it runs on every write, and it does not get tired at 3am the way a code review does.

\`\`\`sql
ALTER TABLE ledger_entries
  ADD CONSTRAINT amount_non_negative
  CHECK (debit >= 0 AND credit >= 0);
\`\`\`

When the database is enforcing your invariants, a bug in your application code becomes a failed write instead of a corrupted balance. That is exactly the trade you want.

## Idempotency, because the network will retry

Here is a fact that never stops being true: the same payment request will arrive more than once. A client times out and retries. A user double-clicks. A queue redelivers because it never saw the acknowledgement. None of these are bugs, they are the normal behaviour of unreliable networks, and if your system processes a retry as a second payment, you have just moved the money twice.

The fix is an idempotency key. The client sends a unique key with each genuine operation, and the database enforces that the key can only be used once.

\`\`\`sql
-- The unique constraint turns a retry into a no-op, not a double charge
INSERT INTO transactions (idempotency_key, amount, status)
VALUES ($1, $2, 'pending')
ON CONFLICT (idempotency_key) DO NOTHING;
\`\`\`

The subtlety that bites people is doing the check in two steps: look for the key, then act. Two copies of the request can both look, both find nothing, and both proceed. The window is tiny and at scale tiny windows happen all day. Let the database settle it atomically with a unique constraint, so the second write fails on the constraint instead of racing past your check. The database is built to resolve these conflicts correctly. Hand-rolled logic is not.

## Reconcile, because you will still be wrong

Even with all of this, assume you will eventually have a discrepancy. An integration with an external provider drifts. A bug slips through a gap you did not anticipate. The mature move is not to believe you are perfect. It is to build the process that catches you when you are not.

We ran automated reconciliation jobs that compared our ledger against the providers we settled with, every day, and flagged any difference for a human before it could compound. This is the difference between a manageable problem and a forensic one. A discrepancy caught the same day is an annoyance you investigate with full context. The same discrepancy caught at month end, after thousands more transactions have piled on top of it, is an archaeology project.

## What I check before a ledger ships

The list I actually run through:

- Every money movement is at least two entries that sum to zero.
- Balances are derived from entries, never stored as an authoritative column.
- Both sides of every transfer are written inside one database transaction.
- Database constraints enforce the invariants, not just application code.
- Every money-moving write carries an idempotency key with a unique constraint.
- A daily reconciliation job compares the ledger against external sources of truth.
- There is a way to recompute any balance from the raw entries on demand.

If I cannot tick all of those, the system is not ready to touch real money.

## The mindset transfers everywhere

Most software is not a ledger, but the habits a ledger forces on you are good everywhere. Make correctness checkable instead of hoped for. Derive state from a source of truth instead of duplicating it. Lean on the guarantees your database already gives you for free. Design for the retry, because it is coming whether you plan for it or not. Assume you will be wrong, and build the thing that tells you when.

You do not need to be in fintech to write code this way. You just need to decide that being able to prove your system is correct is worth more than the shortcut that makes it feel fast today. That ticket about 1,200 naira taught me the lesson the expensive way. The ledger is just where the lesson is least forgiving.
`,
};

export default post;
